import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { apiResponse } from 'src/app/models/apiResponse.model';
import { ApiFilter, FieldsSelection, OrderByConfig } from 'src/app/models/apiParams.model';
import {
    registerForVehicleSpecs,
    addApplicationSetting,
    addCompanySetting,
    updateRegistration,
    updateSettingType,
    updateApplicationSetting,
    updateCompanySetting
} from 'src/app/models/request.model';
import { BaseApiService } from '../base/base-api.service';

/**
 * Vehicle Specs Service - Handles vehicle specs applications, registrations, and settings
 */
@Injectable({ providedIn: 'root' })
export class VehicleSpecsService extends BaseApiService {
    private readonly applicationPath = '/vehicle-specs/v1/application';
    private readonly applicationSettingPath = '/vehicle-specs/v1/application-setting/';
    private readonly registrationPath = '/vehicle-specs/v1/registration/';
    private readonly companySettingPath = '/vehicle-specs/v1/company-setting/';
    private readonly settingTypePath = '/vehicle-specs/v1/setting-type/';

    constructor(http: HttpClient) {
        super(http);
    }

    /*-------------------------
    == APPLICATION CALLS ==
    -------------------------*/

    getVehicleSpecsApplications(limit: number, page: number, orderBy?: string | OrderByConfig[], filter?: string | ApiFilter, fields?: FieldsSelection, defaultFilter?: any): Observable<apiResponse> {
        return this.getAll<apiResponse>(this.applicationPath, { limit, page, orderBy, filter, fields, defaultFilter });
    }

    getVehicleSpecsApplication(id: number | string, fields?: FieldsSelection): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.applicationPath}/`, id, { fields });
    }

    getVehicleSpecsApplicationSetting(id: number | string, fields?: FieldsSelection): Observable<apiResponse> {
        return this.getById<apiResponse>(this.applicationSettingPath, id, { fields });
    }

    getApplicationSettings(applicationId: number): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.applicationPath}/${applicationId}/setting`);
    }

    /*-------------------------
    == REGISTRATION CALLS ==
    -------------------------*/

    getVehicleSpecsRegistrations(limit: number, page: number, orderBy?: string | OrderByConfig[], filter?: string | ApiFilter, fields?: FieldsSelection, defaultFilter?: any): Observable<apiResponse> {
        return this.getAll<apiResponse>(this.registrationPath, { limit, page, orderBy, filter, fields, defaultFilter });
    }

    getVehicleSpecsCompanySettings(id: number | string, fields?: FieldsSelection): Observable<apiResponse> {
        return this.getById<apiResponse>(this.companySettingPath, id, { fields });
    }

    getRegistrationSettings(registrationId: number): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.registrationPath}${registrationId}/setting`);
    }

    registerForVehicleSpecs(data: registerForVehicleSpecs): Observable<apiResponse> {
        return this.post<apiResponse>(this.registrationPath, data, 'json');
    }

    updateRegistration(id: number, data: updateRegistration): Observable<apiResponse> {
        return this.put<apiResponse>(this.registrationPath, id, data, 'json');
    }

    unregisterFromVehicleSpecs(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>(this.registrationPath, id);
    }

    /*-------------------------
    == COMPANY SETTING CALLS ==
    -------------------------*/

    addCompanySetting(data: addCompanySetting): Observable<apiResponse> {
        return this.post<apiResponse>(this.companySettingPath, data, 'json');
    }

    updateCompanySetting(id: number, data: updateCompanySetting): Observable<apiResponse> {
        return this.put<apiResponse>(this.companySettingPath, id, data, 'json');
    }

    deleteCompanySetting(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>(this.companySettingPath, id);
    }

    /*-------------------------
    == APPLICATION SETTING CALLS ==
    -------------------------*/

    addApplicationSetting(data: addApplicationSetting): Observable<apiResponse> {
        return this.post<apiResponse>(this.applicationSettingPath, data, 'json');
    }

    updateApplicationSetting(id: number, data: updateApplicationSetting): Observable<apiResponse> {
        return this.put<apiResponse>(this.applicationSettingPath, id, data, 'json');
    }

    deleteApplicationSetting(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>(this.applicationSettingPath, id);
    }

    /*-------------------------
    == SETTING TYPE CALLS ==
    -------------------------*/

    updateSettingType(id: number, data: updateSettingType): Observable<apiResponse> {
        return this.put<apiResponse>(this.settingTypePath, id, data, 'json');
    }
}
